import React, {useEffect, useState} from 'react';
import {useParams, Link, Navigate} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CTAButton from '../components/CTAButton';
import SEO from '../components/SEO';
import {SERVICES, BUSINESS} from '../config/business';
import {MEDIA} from '../config/media';

const {FiArrowLeft, FiArrowRight, FiCheck, FiPlus, FiMinus, FiChevronRight, FiTarget, FiZap, FiShield, FiLayers} = FiIcons;

function ServiceDetail() {
  const {slug} = useParams();
  const [openFaq, setOpenFaq] = useState(null);
  const service = SERVICES.find(s => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!service?.faq?.length) return undefined;

    const faqPageSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": service.faq.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    };

    const scriptId = 'service-faq-jsonld';
    let faqScript = document.getElementById(scriptId);

    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.id = scriptId;
      faqScript.type = 'application/ld+json';
      document.head.appendChild(faqScript);
    }

    faqScript.textContent = JSON.stringify(faqPageSchema);

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [slug, service]);

  if (!service) return <Navigate to="/services" replace />;

  const serviceMediaMap = [
    MEDIA.services.appointment, MEDIA.services.chatbot, MEDIA.services.crm, MEDIA.services.website,
    MEDIA.services.process, MEDIA.services.dashboard, MEDIA.services.documents, MEDIA.services.microsoft
  ];
  const serviceImage = serviceMediaMap[service.id - 1] || MEDIA.hero;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {"@type": "LocalBusiness", "name": BUSINESS.name},
    "areaServed": {"@type": "Country", "name": "Australia"},
    "serviceType": service.name
  };

  return (
    <main className="bg-white text-left">
      <SEO title={`${service.name} Solutions for Small Business Growth | BPAS`} description={service.seoDescription} schema={[serviceSchema]} />

      {/* Hero Section */}
      <section className="bg-slate-50 border-b border-slate-200 py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services" className="inline-flex items-center text-blue-600 font-medium mb-8 hover:text-blue-700 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2 h-4 w-4" /> Back to Services
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{opacity: 0, x: -20}} animate={{opacity: 1, x: 0}}>
              <h1 className="text-3xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                {service.name} Solutions for <span className="text-blue-600 block sm:inline">Small Business Growth</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                {service.overview}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <CTAButton size="lg" className="w-full sm:w-auto px-10">Book a Free Consult</CTAButton>
                <div className="flex items-center px-4 py-3 bg-white rounded-lg border border-slate-200 shadow-sm">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-4">Timeline</span>
                  <span className="text-sm font-bold text-slate-900">{service.timeline}</span>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{opacity: 0, x: 20}} animate={{opacity: 1, x: 0}} className="relative">
              <img src={serviceImage.src} alt={`${service.name} for Australian businesses`} className="rounded-2xl shadow-2xl w-full h-[300px] lg:h-[400px] object-cover" width="600" height="400" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Who This Is For</h2>
                <p className="text-slate-600 leading-relaxed">
                  This {service.name.toLowerCase()} solution is specifically designed for Australian small business owners and managers who are feeling the weight of operational inefficiencies. Whether you are running a trade business in Sydney, a medical clinic in Brisbane, or a consultancy in Melbourne, this system helps you reclaim your time.
                </p>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">The Problems We Solve</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Eliminate double-entry of data", "Speed up response times to enquiries", "Improve accuracy of quoting and invoicing", "Ensure consistent customer follow-ups"
                  ].map((p, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <SafeIcon icon={FiZap} className="text-blue-600 shrink-0" />
                      <span className="text-sm font-medium text-slate-700">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-slate-900 text-white p-8 sm:p-12 rounded-3xl shadow-xl">
              <h2 className="text-2xl font-bold mb-6">The BPA Solutions Approach</h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                We are a Sydney-based Australian business that understands the local landscape. We don't just set up software; we build a strategic foundation for your growth.
              </p>
              <CTAButton className="w-full">Request a Custom Roadmap</CTAButton>
            </div>
          </div>
        </div>
      </section>

      {/* Website Modernisation Quiz CTA - Premium Websites Only */}
      {service.slug === "premium-websites" && (
        <section className="py-16 bg-slate-50 border-t border-slate-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-10">
              <div className="bg-blue-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiTarget} className="h-7 w-7 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Website Modernisation Quiz</h2>
              <h3 className="text-xl font-bold text-slate-900 mb-5">Already have a website? Check whether it is helping or hurting growth.</h3>
              <p className="text-slate-600 mb-5 leading-relaxed max-w-2xl mx-auto">
                Take the short quiz to review your current website's enquiry flow, mobile experience, trust signals, and automation readiness.
              </p>
              <p className="text-sm text-slate-500 mb-8 max-w-xl mx-auto leading-relaxed">
                If your business is a good fit, BPA Solutions may provide a complimentary website improvement concept or website review.
              </p>
              <a
                href="https://go.meiro.cc/1233235"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 shadow-lg active:scale-[0.98] mb-4"
              >
                Start the quiz
              </a>
              <p className="text-xs text-slate-400">
                Built for Australian small businesses, trades, construction, service businesses, and professional services.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Core Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">Core Features of Our {service.name}</h2>
            <div className="w-12 h-1 bg-blue-600 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {service.featureCards.map((card, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon name={card.icon.replace('Fi', '')} className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{card.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {service.faq.map((item, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors">
                  <span className="font-bold text-slate-900">{item.q}</span>
                  <SafeIcon icon={openFaq === i ? FiMinus : FiPlus} className="text-slate-400 h-5 w-5" />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div initial={{height: 0}} animate={{height: 'auto'}} exit={{height: 0}} className="overflow-hidden">
                      <div className="p-5 bg-slate-50 border-t border-slate-200 text-slate-600 text-sm leading-relaxed">
                        {item.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Linking Section */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-10">Explore More Solutions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/services" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiLayers} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">All Services</h3>
              <p className="text-sm text-slate-500 mb-4">See our full capability in automation and design.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Browse All <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/blog" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiZap} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Success Blog</h3>
              <p className="text-sm text-slate-500 mb-4">Read real-world automation case studies.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Read Blog <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/resources" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Free Tools</h3>
              <p className="text-sm text-slate-500 mb-4">Checklists to help you audit your own business.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">View Tools <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
            <Link to="/contact" className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col group text-left">
              <SafeIcon icon={FiTarget} className="h-8 w-8 text-blue-600 mb-4" />
              <h3 className="font-bold mb-2">Get Started</h3>
              <p className="text-sm text-slate-500 mb-4">Book your free 20-minute automation review.</p>
              <span className="mt-auto text-blue-600 font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform">Contact Us <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Ready to Scale with {service.name}?</h2>
          <p className="text-slate-300 mb-10 leading-relaxed">Let's discuss how we can implement this solution for your specific needs.</p>
          <CTAButton size="lg" className="w-full sm:w-auto px-12">Book a Free Consult</CTAButton>
        </div>
      </section>
    </main>
  );
}

export default ServiceDetail;